import type { StoryObject } from '@benzinga/advanced-news-manager';
import { SafeError, safeTimeout } from '@benzinga/safe-await';
import type { SafePromise, SafeType } from '@benzinga/safe-await';
import type { Session } from '@benzinga/session';
import { normalizedValue, TtlFifoCache } from '@benzinga/utils';
import type { SimpleNewsOptions, SimpleNewsQuery } from './entities';
import { InternalNewsManager } from './manager';

const defaultSimpleNewsTimeoutMs = (): number => (process.env.NODE_ENV === 'development' ? 8000 : 4000);
const DEFAULT_MAX_LIMIT = 120;
const DEFAULT_CACHE_TTL_MS = 300000;
const AFTER_CACHE_TTL_MS = 60000;
const MAX_CACHE_ENTRIES = 300;
const IMG_PLACEHOLDER = 'https://cdn.benzinga.com/files/images/story/2025/07/18/bz-placeholder.png';
const BZ_PRO_IMG_PLACEHOLDER = 'https://cdn.benzinga.com/files/images/story/2025/07/18/pro-placeholder.png';
const PLACEHOLDER_START_MS = new Date('2025-04-09T00:00:00Z').getTime();
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const queryCache = new TtlFifoCache(MAX_CACHE_ENTRIES);
const inFlightRequests = new Map<string, SafePromise<StoryObject[]>>();

export interface SimplyQueryInternalNewsConfig {
  cache?: boolean;
  cacheTtlMs?: number;
  logPerformance?: boolean;
  maxLimit?: number;
  source?: string;
  timeoutMs?: number;
}

type SimpleNewsQueryOutcome = 'cache_hit' | 'deduped' | 'error' | 'limit_exceeded' | 'success';

const SIMPLE_NEWS_PERF_LOG_OUTCOMES: ReadonlySet<SimpleNewsQueryOutcome> = new Set(['error', 'limit_exceeded']);

interface SimpleNewsQueryPerformanceEvent {
  cache_hit: boolean;
  cacheable: boolean;
  duration_ms: number;
  error_type?: string;
  event: 'simple_news_query';
  in_flight_deduped: boolean;
  limit?: number;
  outcome: SimpleNewsQueryOutcome;
  query_hash: string;
  result_count?: number;
  source?: string;
  timeout_ms: number;
}

function stableQueryKey(query: SimpleNewsQuery, options: SimpleNewsOptions): string {
  return JSON.stringify({
    options: normalizedValue(options ?? {}),
    query: normalizedValue(query ?? {}),
  });
}

function stableRequestKey(
  query: SimpleNewsQuery,
  options: SimpleNewsOptions,
  config: SimplyQueryInternalNewsConfig,
): string {
  return JSON.stringify({
    config: normalizedValue({
      cache: config.cache ?? true,
      cacheTtlMs: config.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS,
      maxLimit: config.maxLimit ?? DEFAULT_MAX_LIMIT,
      timeoutMs: config.timeoutMs ?? defaultSimpleNewsTimeoutMs(),
    }),
    options: normalizedValue(options ?? {}),
    query: normalizedValue(query ?? {}),
  });
}

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function shouldLogPerformance(config: SimplyQueryInternalNewsConfig): boolean {
  return config.logPerformance === true || process.env.SIMPLE_NEWS_PERF_LOGS === 'true';
}

function logPerformanceEvent(config: SimplyQueryInternalNewsConfig, event: SimpleNewsQueryPerformanceEvent): void {
  if (!shouldLogPerformance(config)) return;
  if (!SIMPLE_NEWS_PERF_LOG_OUTCOMES.has(event.outcome)) return;

  console.info(
    JSON.stringify({
      ...event,
      timestamp: new Date().toISOString(),
    }),
  );
}

function isAfterParamRecent(after?: string): boolean {
  if (!after) return false;
  if (DATE_ONLY_PATTERN.test(after)) return after === new Date().toISOString().slice(0, 10);

  const timestamp = new Date(after).getTime();
  if (Number.isNaN(timestamp)) return true;

  return Date.now() - timestamp < AFTER_CACHE_TTL_MS;
}

function isCacheableQuery(query: SimpleNewsQuery, options: SimpleNewsOptions): boolean {
  return !(
    options?.last ||
    (!options?.before && isAfterParamRecent(options?.after)) ||
    query?.body ||
    query?.search ||
    query?.title
  );
}

function normalizeStoryImage(story: StoryObject): StoryObject {
  const createdMs = story.created ? new Date(story.created).getTime() : Number.NaN;
  if (!story.image && !Number.isNaN(createdMs) && createdMs < PLACEHOLDER_START_MS) {
    return {
      ...story,
      image: story.isBzProPost && !story.isBzPost ? BZ_PRO_IMG_PLACEHOLDER : IMG_PLACEHOLDER,
    };
  }
  return story;
}

function normalizeStories(stories: StoryObject[]): StoryObject[] {
  return stories.map(normalizeStoryImage);
}

// queryCache and in-flight dedupe share one canonical StoryObject[] per key; callers may mutate
// the returned array (e.g. .sort, .splice), so each caller needs its own copy.
function copyCachedStoriesResult(response: SafeType<StoryObject[]>): SafeType<StoryObject[]> {
  return response.ok ? { ok: response.ok.slice() } : response;
}

export function createSimpleNewsLimitError(limit: number, maxLimit = DEFAULT_MAX_LIMIT) {
  return new SafeError(
    `Request limit exceeded. Requesting more than ${maxLimit} results is not allowed.`,
    'simple_news_limit_exceeded',
    { limit, maxLimit },
  );
}

/**
 * Server-side only helper for querying news from the advanced news manager using the internal news manager; do not call this from client-side code.
 */
export async function simplyQueryInternalNews(
  session: Session,
  query: SimpleNewsQuery = {},
  options: SimpleNewsOptions = {},
  config: SimplyQueryInternalNewsConfig = {},
): SafePromise<StoryObject[]> {
  const startTime = Date.now();
  const maxLimit = config.maxLimit ?? DEFAULT_MAX_LIMIT;
  const timeoutMs = config.timeoutMs ?? defaultSimpleNewsTimeoutMs();
  const cacheKey = stableQueryKey(query, options);
  const queryHash = hashString(cacheKey);
  const requestKey = stableRequestKey(query, options, config);
  const shouldCache = config.cache !== false && isCacheableQuery(query, options);

  if (options.limit && options.limit > maxLimit) {
    logPerformanceEvent(config, {
      cache_hit: false,
      cacheable: shouldCache,
      duration_ms: Date.now() - startTime,
      error_type: 'simple_news_limit_exceeded',
      event: 'simple_news_query',
      in_flight_deduped: false,
      limit: options.limit,
      outcome: 'limit_exceeded',
      query_hash: queryHash,
      source: config.source,
      timeout_ms: timeoutMs,
    });
    return { err: createSimpleNewsLimitError(options.limit, maxLimit) };
  }

  const cachedStories = shouldCache ? queryCache.get<StoryObject[]>(cacheKey) : undefined;
  if (cachedStories) {
    logPerformanceEvent(config, {
      cache_hit: true,
      cacheable: shouldCache,
      duration_ms: Date.now() - startTime,
      event: 'simple_news_query',
      in_flight_deduped: false,
      limit: options.limit,
      outcome: 'cache_hit',
      query_hash: queryHash,
      result_count: cachedStories.length,
      source: config.source,
      timeout_ms: timeoutMs,
    });
    return { ok: cachedStories.slice() };
  }

  const inFlightRequest = inFlightRequests.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest.then(response => {
      logPerformanceEvent(config, {
        cache_hit: false,
        cacheable: shouldCache,
        duration_ms: Date.now() - startTime,
        error_type: response.err?.type,
        event: 'simple_news_query',
        in_flight_deduped: true,
        limit: options.limit,
        outcome: response.ok ? 'deduped' : 'error',
        query_hash: queryHash,
        result_count: response.ok?.length,
        source: config.source,
        timeout_ms: timeoutMs,
      });
      return copyCachedStoriesResult(response);
    });
  }

  const request = safeTimeout(session.getManager(InternalNewsManager).simplyQueryNews(query, options), timeoutMs).then(
    response => {
      try {
        if (response.ok) {
          const stories = normalizeStories(response.ok);
          if (shouldCache) queryCache.set(cacheKey, stories, config.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS);
          logPerformanceEvent(config, {
            cache_hit: false,
            cacheable: shouldCache,
            duration_ms: Date.now() - startTime,
            event: 'simple_news_query',
            in_flight_deduped: false,
            limit: options.limit,
            outcome: 'success',
            query_hash: queryHash,
            result_count: stories.length,
            source: config.source,
            timeout_ms: timeoutMs,
          });
          return copyCachedStoriesResult({ ok: stories });
        }
        logPerformanceEvent(config, {
          cache_hit: false,
          cacheable: shouldCache,
          duration_ms: Date.now() - startTime,
          error_type: response.err?.type,
          event: 'simple_news_query',
          in_flight_deduped: false,
          limit: options.limit,
          outcome: 'error',
          query_hash: queryHash,
          source: config.source,
          timeout_ms: timeoutMs,
        });
        return response;
      } catch (error) {
        logPerformanceEvent(config, {
          cache_hit: false,
          cacheable: shouldCache,
          duration_ms: Date.now() - startTime,
          error_type: error instanceof SafeError ? error.type : 'simple_news_normalization_failed',
          event: 'simple_news_query',
          in_flight_deduped: false,
          limit: options.limit,
          outcome: 'error',
          query_hash: queryHash,
          source: config.source,
          timeout_ms: timeoutMs,
        });
        return {
          err:
            error instanceof SafeError
              ? error
              : new SafeError('Failed to normalize simple news response', 'simple_news_normalization_failed', error),
        };
      }
    },
  );

  inFlightRequests.set(requestKey, request);
  request.finally(() => inFlightRequests.delete(requestKey));

  return request;
}
